import React, { PureComponent } from 'react';
import { WithTranslation, withTranslation } from 'react-i18next';
import { connect } from 'react-redux';
import { RouteComponentProps, withRouter } from 'react-router-dom';
import { ids } from 'config';
import { compose } from 'redux';
import {
  change,
  destroy,
  Field,
  Form,
  getFormValues,
  InjectedFormProps,
  reduxForm,
} from 'redux-form';
import { getAddons, getPurchaseItems } from 'store/cashiering/selectors';
import {
  fetchReservation,
  updateReservationPurchases,
} from 'store/reservation/actions';
import {
  getAllAddedPurchaseElements,
  getBreakdown,
  getReservation,
  isReservationDayUse,
} from 'store/reservation/selectors';
import { PurchaseItem } from 'types/Api/Availability';
import {
  AllPurchaseItems,
  Breakdown,
  ReservationView,
  UpdatePurchaseFormData,
} from 'types/Api/Reservation';
import Store from 'types/Store';
import { Configurator, Router } from 'utils';
import { Path } from 'utils/Router';

import { Grid } from '@material-ui/core';
import { WithStyles, withStyles } from '@material-ui/styles';

import AddonsMenuElement from '../AddonsMenuElement';
import { updateFormValuesByTransactionId } from '../store/actions';
import {
  getConfirmedNewPurchaseOrders,
  getOnlyPreAdded,
  getPurchaseElementsFromFirstNightRate,
  getPurchaseElementsFromLastNightRate,
} from '../store/selectors';
import { FormValues } from '../types';

import styles from './AddonsMenu.style';
import {
  getBreakdownDataPerPerson,
  getBreakdownDataPerReservation,
  mergeDataWithPreAddedItems,
  prepareDataForNewAddedItems,
} from './purchaseElementsUtils';

const NUMBER_TO_ADD = 1;
const NUMBER_TO_REMOVE = -1;

interface PassedProps {
  showAlreadyAdded?: boolean;
  addons: PurchaseItem[];
  purchaseItems: PurchaseItem[];
}

interface AddonsMenuProps
  extends PassedProps,
    WithTranslation,
    InjectedFormProps,
    RouteComponentProps,
    WithStyles<typeof styles> {
  allPreAddedPurchases: AllPurchaseItems;
  confirmedNewPurchaseOrders: PurchaseItem[];
  onChange: (form: string, field: string, value: any) => void;
  onDestroy: (form: string) => void;
  orderedItems?: any;
  breakdown: Breakdown;
  reservation: ReservationView;
  updateFormValuesByTransactionId: typeof updateFormValuesByTransactionId;
  fetchReservation: typeof fetchReservation;
  updateReservationPurchases: typeof updateReservationPurchases;
  getOnlyPreAdded: typeof getOnlyPreAdded;
  isPreAddedElement: boolean;
  onlyPreAdded: PurchaseItem[];
  purchaseElementsFromFirstNightRate: PurchaseItem[];
  purchaseElementsFromLastNightRate: PurchaseItem[];
  isReservationDayUse: boolean;
}

interface AddonsMenuState {
  isLoading: boolean;
  isPreAddedElement: boolean;
  showAlreadyAdded: boolean;
  previousFormValue: FormValues;
}

class AddonsMenu extends PureComponent<AddonsMenuProps, AddonsMenuState> {
  public state: AddonsMenuState = {
    isLoading: true,
    isPreAddedElement: false,
    showAlreadyAdded: false,
    previousFormValue: {},
  };

  public async componentDidMount() {
    const {
      reservation: { id },
      fetchReservation,
      updateFormValuesByTransactionId,
      orderedItems,
    } = this.props;

    if (orderedItems) this.setState({ previousFormValue: orderedItems });
    await fetchReservation(id);
    await updateFormValuesByTransactionId(ids.ADDONS_FORM, id);
  }

  public render() {
    const { handleSubmit, classes } = this.props;

    return (
      <Form onSubmit={handleSubmit(this.onSubmit)}>
        <Grid container className={classes.addonsWrapper} spacing={2}>
          {this.renderItems()}
        </Grid>
      </Form>
    );
  }

  public onItemClick = (
    itemName: string,
    shouldRemove: boolean,
    numberToAdd: number = NUMBER_TO_ADD,
    numberToRemove: number = NUMBER_TO_REMOVE
  ) => {
    const { onChange, orderedItems } = this.props;
    const isFieldRegistered = Boolean(orderedItems && orderedItems[itemName]);
    const attribute = shouldRemove ? numberToRemove : numberToAdd;
    const value = isFieldRegistered
      ? orderedItems[itemName].quantity + attribute
      : attribute;
    onChange(ids.ADDONS_FORM, itemName, {
      transactionId: isFieldRegistered
        ? orderedItems[itemName].transactionId
        : undefined,
      quantity: value,
    });
  };

  public onItemAdd = (itemName: string, numberToAdd?: number) =>
    this.onItemClick(itemName, false, numberToAdd);

  public onItemRemove = (itemName: string, numberToRemove?: number) =>
    this.onItemClick(itemName, true, undefined, numberToRemove);

  public onDeleteClick = (id: string) => {
    const { onChange, orderedItems } = this.props;

    const isFieldRegistered = Boolean(orderedItems && orderedItems[id]);
    onChange(ids.ADDONS_FORM, id, {
      transactionId: isFieldRegistered
        ? orderedItems[id].transactionId
        : undefined,
      quantity: 0,
    });
  };

  private getElements = () => {
    const {
      onlyPreAdded,
      purchaseElementsFromFirstNightRate,
      purchaseElementsFromLastNightRate,
      addons,
      showAlreadyAdded,
    } = this.props;

    const checkInElements = [
      ...purchaseElementsFromFirstNightRate,
      ...onlyPreAdded,
    ];
    const checkOutElements = [
      ...purchaseElementsFromLastNightRate,
      ...onlyPreAdded,
    ];
    const isCheckInPath = Router.currentPath === Path.checkIn;
    const alreadyAddedElements = isCheckInPath
      ? checkInElements
      : checkOutElements;

    return showAlreadyAdded ? alreadyAddedElements : addons;
  };

  private renderItems = () => {
    const { orderedItems, allPreAddedPurchases, showAlreadyAdded } = this.props;
    const elements = this.getElements();

    return elements.map((elem, index) => {
      const { id, code, fromRate: isElementFromRate } = elem;
      const backgroundUrl = Configurator.getPurchaseElementImage(code)?.content;

      return (
        <Grid item xs={6} md={4} lg={3} xl={2} key={`${id}- ${index}`}>
          <Field
            name={id}
            data={elem}
            component={AddonsMenuElement}
            onItemAdd={this.onItemAdd}
            backgroundUrl={showAlreadyAdded ? null : backgroundUrl}
            onItemRemove={this.onItemRemove}
            onItemDelete={this.onDeleteClick}
            isPreAddedElement={showAlreadyAdded || false}
            quantity={orderedItems && orderedItems[id]?.quantity}
            isElementFromRate={showAlreadyAdded ? isElementFromRate : false}
            allPreAddedPurchases={showAlreadyAdded && allPreAddedPurchases}
          />
        </Grid>
      );
    });
  };

  private prepareUpdatePurchaseFormData = (formValues: FormValues) => {
    const {
      purchaseItems,
      isReservationDayUse,
      breakdown,
      onDestroy,
      allPreAddedPurchases: { preAddedPurchaseElements },
    } = this.props;
    const { previousFormValue } = this.state;

    const parsedNewFormValues = prepareDataForNewAddedItems(
      formValues,
      purchaseItems,
      previousFormValue
    );

    const mergedPurchaseElementsData = mergeDataWithPreAddedItems(
      parsedNewFormValues,
      preAddedPurchaseElements
    );

    if (!Object.values(formValues).some((item) => !!item.quantity))
      onDestroy(ids.ADDONS_FORM);

    return mergedPurchaseElementsData.reduce(
      (
        prev: UpdatePurchaseFormData,
        { purchaseElementId, id, dateAnchor, quantity }
      ) => {
        if (!quantity && id) {
          prev.removed.push(id);
        } else {
          const item = purchaseItems.find(
            (item) => item.id === purchaseElementId
          )!;
          const { isPerPerson } = item;

          const preparedBrakedown = isPerPerson
            ? getBreakdownDataPerPerson(
                item,
                quantity,
                isReservationDayUse,
                breakdown
              )
            : getBreakdownDataPerReservation(
                item,
                quantity,
                isReservationDayUse,
                breakdown
              );

          prev.modified.push({
            ...(id && { id }),
            purchaseElementId,
            dateAnchor,
            breakdown: preparedBrakedown,
          });
        }

        return prev;
      },
      {
        modified: [],
        removed: [],
      }
    );
  };

  private onSubmit = async (formValues: FormValues) => {
    const updateFormData = this.prepareUpdatePurchaseFormData(formValues);
    await this.updatePurchases(updateFormData);
    this.changeRoute(Router.nextStepURL);
  };

  private updatePurchases = async (result: UpdatePurchaseFormData) => {
    const { reservation } = this.props;
    const { updateReservationPurchases } = this.props;

    return updateReservationPurchases(reservation, result);
  };

  private changeRoute = (route: string) => {
    const { history } = this.props;
    history.push(route);
  };
}

const mapStateToProps = (state: Store, props: AddonsMenuProps) => ({
  confirmedNewPurchaseOrders: getConfirmedNewPurchaseOrders(state),
  orderedItems: getFormValues(ids.ADDONS_FORM)(state),
  breakdown: getBreakdown(state),
  reservation: getReservation(state),
  allPreAddedPurchases: getAllAddedPurchaseElements(state),
  onlyPreAdded: getOnlyPreAdded(state),
  purchaseItems: getPurchaseItems(state),
  addons: getAddons(state, props),
  purchaseElementsFromFirstNightRate:
    getPurchaseElementsFromFirstNightRate(state),
  purchaseElementsFromLastNightRate:
    getPurchaseElementsFromLastNightRate(state),
  isReservationDayUse: isReservationDayUse(state),
});

const mapDispatchToProps = {
  updateReservationPurchases,
  updateFormValuesByTransactionId,
  fetchReservation,
  onDestroy: destroy,
  onChange: change,
};

export default compose(
  withRouter,
  withTranslation(),
  withStyles(styles),
  reduxForm({
    form: ids.ADDONS_FORM,
    destroyOnUnmount: false,
  }),
  connect(mapStateToProps, mapDispatchToProps)
)(AddonsMenu) as (props: PassedProps) => JSX.Element;
